import { createContext, useContext, useMemo, useRef, type HTMLAttributes, type ReactNode, type RefObject } from "react";
import type { PresentationName, Theme } from "./tokens.ts";

export type { PresentationName, Theme };

/** Joins class names, skipping falsy values. */
export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(" ");
}

interface PresentationContextValue {
  presentation: PresentationName;
  /** Where popups (dialog, sheet, select, toast) portal to, so they inherit the root's tokens. */
  portalContainer: RefObject<HTMLDivElement | null> | null;
}

const PresentationContext = createContext<PresentationContextValue>({ presentation: "mobile", portalContainer: null });

/** The presentation of the nearest `<PresentationRoot>`; "mobile" outside one. */
export function usePresentation(): PresentationName {
  return useContext(PresentationContext).presentation;
}

/** @internal Portal target for popups inside the nearest root. */
export function usePortalContainer(): RefObject<HTMLDivElement | null> | undefined {
  return useContext(PresentationContext).portalContainer ?? undefined;
}

export interface PresentationRootProps extends HTMLAttributes<HTMLDivElement> {
  presentation: PresentationName;
  /** Pin a theme; omit to follow the OS. */
  theme?: Theme;
  children?: ReactNode;
}

/**
 * Sets `data-presentation` (and optionally `data-theme`) and hosts popups, so every component
 * inside, including portalled ones, picks up the right tokens. Wrap each app once.
 */
export function PresentationRoot({ presentation, theme, className, children, ...rest }: PresentationRootProps) {
  const portalContainer = useRef<HTMLDivElement>(null);
  const value = useMemo(() => ({ presentation, portalContainer }), [presentation]);
  return (
    <PresentationContext.Provider value={value}>
      <div {...rest} className={cx("pmu-root", className)} data-presentation={presentation} data-theme={theme}>
        {children}
        <div ref={portalContainer} className="pmu-portal-host" />
      </div>
    </PresentationContext.Provider>
  );
}
